import { storeToRefs } from "pinia";
import { type WhereClause } from "spraypaint";

import { MediaRequest as MediaRequestResource } from "shared/resources";
import useUserStore from "shared/stores/user";
import { type MediaRequest as MentionMediaRequest } from "shared/types/mentions";

const TV_TYPES = ["TvChannel", "TvCaption"];
const EXTERNAL_MEDIA_REQUEST_ORIGINS = ["Kinetiq", "TvEyes"];
type MediaRequestProviderIdentity = Partial<
  Pick<MentionMediaRequest, "broadcast_provider" | "origin_keyword">
>;

export const DEFAULT_TVEYES_RADIO_IMAGE_URL = `${import.meta.env.VITE_ASSETS_URL}/images/radio-poster.png`;

function isTvEyesMediaRequest(
  mediaRequest: MediaRequestProviderIdentity
): boolean {
  if (mediaRequest.broadcast_provider) {
    return mediaRequest.broadcast_provider.name === "tveyes";
  }

  return mediaRequest.origin_keyword === "TvEyes";
}

function isKinetiqMediaRequest(
  mediaRequest: MediaRequestProviderIdentity
): boolean {
  if (mediaRequest.broadcast_provider !== undefined) {
    return mediaRequest.broadcast_provider?.name === "kinetiq";
  }

  return mediaRequest.origin_keyword === "Kinetiq";
}

function isExternalMediaRequest(
  mediaRequest: MediaRequestProviderIdentity
): boolean {
  if (mediaRequest.broadcast_provider) return true;

  return EXTERNAL_MEDIA_REQUEST_ORIGINS.includes(
    mediaRequest.origin_keyword ?? ""
  );
}

function mapFromResource(
  mediaRequestResource: MediaRequestResource
): MentionMediaRequest {
  return {
    broadcast_provider: mediaRequestResource.broadcastProvider,
    created_at: mediaRequestResource.createdAt,
    id: Number(mediaRequestResource.id),
    instant_download: mediaRequestResource.instantDownload,
    media_url: mediaRequestResource.mediaUrl,
    origin_keyword: mediaRequestResource.originKeyword,
    source_type: mediaRequestResource.sourceType,
    status: mediaRequestResource.status,
    thumbnail_url: mediaRequestResource.thumbnailUrl,
    transcript: mediaRequestResource.transcript,
    user: {
      id: Number(mediaRequestResource.user.id),
      name: mediaRequestResource.user.name,
    },
  };
}

function sourceType(mediaRequest: MentionMediaRequest): string {
  if (TV_TYPES.includes(mediaRequest.source_type)) return "video";

  return "audio";
}

function tvEyesMediaRequestThumbnailUrl(
  mediaRequest: MentionMediaRequest
): string {
  if (sourceType(mediaRequest) === "audio")
    return DEFAULT_TVEYES_RADIO_IMAGE_URL;

  return mediaRequest.thumbnail_url;
}

function isMediaRequestProcessed(
  mediaRequest: Pick<MediaRequestResource | MentionMediaRequest, "status">
) {
  return mediaRequest.status !== "In Progress";
}

function isMediaRequestCompleted(
  mediaRequest: Pick<MediaRequestResource | MentionMediaRequest, "status">
) {
  return mediaRequest.status === "Completed";
}

function isMediaRequestCancelled(
  mediaRequest: MediaRequestResource | MentionMediaRequest
) {
  return mediaRequest.status === "Cancelled";
}

function resourceQueryForMediaRequestsTab() {
  const extraAttributes = {
    media_requests: ["audience"],
    radio_clips: [
      "type", // for translations in share & push to team modals
      // for radio clips mobile sharing "text" is necessary too, but it is regular attribute, not an extra attribute
      "tveyes_player_url",
      "tveyes_slim_player_url",
      "tveyes_editor_url",
    ],
    tv_captions: [
      "type", // for translations in share & push to team modals
      "text", // for mobile sharing
      "tveyes_player_url",
      "tveyes_slim_player_url",
      "tveyes_editor_url",
    ],
  };

  return MediaRequestResource.includes([
    "user",
    "accountManager",
    "programAiring",
    "viewedItems",
    { source: ["source", "location", "source.location"] },
  ]).selectExtra(extraAttributes);
}

async function fetchCurrentUserMediaRequests(
  query?: WhereClause
): Promise<Array<MediaRequestResource>> {
  const { currentUser } = storeToRefs(useUserStore());

  const response = await resourceQueryForMediaRequestsTab()
    .where({ ...(query || {}), userId: currentUser.value.id })
    .order({ createdAt: "desc", id: "desc" })
    .all();

  return response.data || [];
}

export {
  fetchCurrentUserMediaRequests,
  isExternalMediaRequest,
  isKinetiqMediaRequest,
  isMediaRequestCancelled,
  isMediaRequestCompleted,
  isMediaRequestProcessed,
  isTvEyesMediaRequest,
  mapFromResource,
  resourceQueryForMediaRequestsTab,
  sourceType,
  tvEyesMediaRequestThumbnailUrl,
};
